import type { FloraCatalog } from '@/flora/FloraCatalog';
import type { FloraDefinition } from '@/flora/FloraDefinition';
import { FLORA_CATEGORIES } from '@/flora/FloraDefinition';
import type { SimpleSeason } from '@/environment/EnvironmentState';
import { clear, el, formatLength, setClass, type Child } from './dom';
import { filledIcon, icon } from './Icons';
import { FloraCard } from './FloraCard';
import { floraThumbnailSvg } from './FloraThumbnail';
import type { UiPreferences } from './UiPreferences';
import {
  COLOR_FAMILIES,
  COLOR_FAMILY_SWATCHES,
  FLORA_GROUPINGS,
  FLORA_SORTS,
  FORM_FACTORS,
  activeFilterCount,
  categoryLabel,
  colorFamiliesOf,
  colorFamilyLabel,
  defaultFloraFilter,
  displayName,
  filterFlora,
  formFactorLabel,
  formFactorOf,
  genusFacets,
  groupFlora,
  nativeStatusOf,
  ratingOf,
  sortFlora,
  sortNeedsLocalization,
  tagFacets,
  tallyForSite,
  toggleFacet,
  type ColorFamily,
  type FilterContext,
  type FloraFilter,
  type FloraGroupBy,
  type FloraSort,
  type FormFactor,
} from './FloraFacets';
import type { FloraResolver } from '@/localization/FloraResolver';
import {
  primaryWarning,
  suitabilityBadge,
  suitabilityLabel,
  type SuitabilityRating,
} from '@/localization/Suitability';

/**
 * Left-hand flora palette (spec §19, milestone 6.4): search, faceted filters, grouped thumbnail
 * grid, favorites, and recently used.
 *
 * All browse logic lives in `FloraFacets.ts`; this class is the rendering shell. The filter state is
 * treated as immutable — every mutation produces a new {@link FloraFilter} and re-renders the
 * results, which keeps the panel's behaviour trivially predictable at this catalog size.
 */

export interface FloraBrowserDeps {
  catalog: FloraCatalog;
  preferences: UiPreferences;
  /** Current season, so thumbnails match what the canvas is showing. */
  season: () => SimpleSeason;
  /** The scene's day of year, which drives the record card's 3D preview. */
  dayOfYear?: () => number;
  /** The plant currently armed for placement. */
  activeId: () => string;
  onPick: (definitionId: string) => void;
  onShutChange?: (shut: boolean) => void;
  /**
   * Where the plant record card mounts. It parks itself beside the palette against the viewport, so
   * it cannot live inside this panel — the panel blurs its backdrop, which would trap a fixed child
   * inside the 300px column. Defaults to the panel's own parent when not given.
   */
  cardHost?: () => HTMLElement;
  /**
   * The site's flora resolver, when the site has been localized.
   *
   * Optional, and its absence is the whole of the unlocalized experience: no badges, no site summary,
   * no suitability facets. A palette that showed grey "unrated" chips on every tile would be
   * advertising a feature rather than serving one.
   */
  resolver?: () => FloraResolver | null;
  /**
   * Open the full flora database.
   *
   * The button sits in this panel's own header rather than in the toolbar, because the database is
   * *this* panel at full size: the same catalog, the same query model, room to compare. A control
   * that opens a bigger version of what you are looking at belongs on the thing it enlarges. Omitted
   * leaves the header exactly as it was, with no dead button.
   */
  onOpenDatabase?: () => void;
}

export class FloraBrowser {
  readonly root: HTMLElement;

  private filter: FloraFilter = defaultFloraFilter();
  private sort: FloraSort = 'common-name';
  private groupBy: FloraGroupBy = 'genus';
  private filtersOpen = false;

  private readonly searchInput: HTMLInputElement;
  private readonly formRail: HTMLElement;
  private readonly filtersEl: HTMLElement;
  private readonly filterToggle: HTMLButtonElement;
  private readonly recentEl: HTMLElement;
  private readonly resultsEl: HTMLElement;
  private readonly countEl: HTMLElement;
  private readonly siteSummary: HTMLElement;
  private readonly metaEl: HTMLElement;
  private readonly body: HTMLElement;
  private readonly card: FloraCard;

  constructor(private readonly deps: FloraBrowserDeps) {
    this.card = new FloraCard({
      host: () => deps.cardHost?.() ?? this.root.parentElement ?? document.body,
      preferences: deps.preferences,
      season: () => deps.season(),
      ...(deps.dayOfYear ? { dayOfYear: (): number => deps.dayOfYear?.() ?? 172 } : {}),
      onPick: (id) => deps.onPick(id),
      // The card can star a plant too, and the tile it came from has a star of its own to update.
      onFavoriteChange: () => this.renderResults(),
      // Escape and outside clicks close the card without the palette hearing about it otherwise.
      onOpenChange: () => this.markInfoActive(),
      ...(deps.resolver ? { resolver: deps.resolver } : {}),
    });

    this.countEl = el('span', { class: 'lg-panel__count' });
    this.searchInput = el('input', {
      class: 'lg-search__input',
      attrs: {
        type: 'search',
        placeholder: 'Search plants, genus, tags…',
        'aria-label': 'Search the flora catalog',
      },
      on: {
        input: () => this.setFilter({ search: this.searchInput.value }),
      },
    });

    this.formRail = el('div', { class: 'lg-rail', attrs: { role: 'group', 'aria-label': 'Form factor' } });
    this.filtersEl = el('div', { class: 'lg-filters', attrs: { hidden: true } });
    this.filterToggle = el('button', {
      class: 'lg-chip lg-chip--ghost',
      attrs: { type: 'button' },
      on: { click: () => this.toggleFilters() },
    });
    this.recentEl = el('div', { class: 'lg-browser__recent' });
    this.resultsEl = el('div', { class: 'lg-browser__results' });
    this.siteSummary = el('div', { class: 'lg-browser__site', attrs: { hidden: true } });
    this.metaEl = el('div', { class: 'lg-browser__meta' });

    this.body = el(
      'div',
      { class: 'lg-panel__body' },
      el(
        'div',
        { class: 'lg-search' },
        el('span', { class: 'lg-search__icon', html: icon('search', 16) }),
        this.searchInput,
      ),
      this.siteSummary,
      this.formRail,
      this.metaEl,
      this.filtersEl,
      this.recentEl,
      this.resultsEl,
    );

    this.root = el(
      'aside',
      {
        class: 'lg-panel lg-panel--left lg-panel--palette',
        attrs: { 'aria-label': 'Flora palette' },
      },
      el(
        'div',
        { class: 'lg-panel__head' },
        el('span', { class: 'lg-panel__icon', html: icon('plant', 16) }),
        el('h2', { class: 'lg-panel__title', text: 'Flora' }),
        this.countEl,
        deps.onOpenDatabase
          ? el(
              'button',
              {
                class: 'lg-panel__database',
                title: 'Open the flora database — the whole catalog, compared and charted',
                attrs: { type: 'button', 'aria-label': 'Open the flora database' },
                on: { click: () => deps.onOpenDatabase?.() },
              },
              el('span', { class: 'lg-panel__databaseicon', html: icon('layers', 13) }),
              el('span', { text: 'Database' }),
            )
          : null,
        el('button', {
          class: 'lg-panel__collapse',
          html: icon('chevron', 16),
          title: 'Shut the flora palette',
          attrs: { type: 'button', 'aria-label': 'Shut the flora palette' },
          on: { click: () => this.setShut(!this.isShut) },
        }),
      ),
      this.body,
    );

    this.setShut(deps.preferences.panelShut('flora'));
    this.renderMeta();
    this.renderFormRail();
    this.renderFilters();
    this.renderResults();
  }

  /**
   * The sort and group row.
   *
   * Rebuilt rather than built once, because "Best for this site" appears the moment a site is
   * localized and a `<select>` populated in the constructor would never gain the option. Cheap: two
   * selects and a chip.
   */
  private renderMeta(): void {
    clear(this.metaEl);
    const localized = (this.deps.resolver?.() ?? null) !== null;
    // A sort that stopped being offered — the site was un-localized — falls back rather than leaving
    // the palette in an order no control can now express.
    if (!localized && sortNeedsLocalization(this.sort)) this.sort = 'common-name';

    this.metaEl.append(
      this.filterToggle,
      this.select(
        'Sort',
        FLORA_SORTS.filter((option) => localized || !sortNeedsLocalization(option.value)),
        this.sort,
        (value) => {
          this.sort = value;
          this.renderResults();
        },
      ),
      this.select('Group', FLORA_GROUPINGS, this.groupBy, (value) => {
        this.groupBy = value;
        this.renderResults();
      }),
    );
  }

  /**
   * Whether the palette is collapsed to its header row.
   *
   * *Shut*, not *collapsed*: the left dock stacks three palettes, one per placeable class, and the
   * gesture a stack wants is "give your height back to the others", not the 44px rail the right dock
   * still uses. See `.lg-panel.is-shut` in `hud.css`.
   */
  get isShut(): boolean {
    return this.root.classList.contains('is-shut');
  }

  setShut(shut: boolean): void {
    setClass(this.root, 'is-shut', shut);
    this.deps.preferences.setPanelShut('flora', shut);
    this.deps.onShutChange?.(shut);
    // The record card is anchored to a tile a shut palette no longer shows.
    if (shut) this.card.close();
  }

  /** Re-render everything that depends on outside state (season, favorites, localization). */
  refresh(): void {
    this.renderMeta();
    this.renderFormRail();
    // The suitability facet appears and disappears with the site, so the filter panel is rebuilt
    // too — not only the results it produces.
    this.renderFilters();
    this.renderResults();
    // Season and unit changes reach the open record card too — it draws the same silhouettes.
    this.card.refresh();
  }

  private get definitions(): FloraDefinition[] {
    return this.deps.catalog.list();
  }

  /**
   * What the browse model needs to know about the outside world.
   *
   * Rebuilt per render rather than cached, because the resolver behind it can be swapped by a
   * localization at any moment and a stale context would rate plants against the previous climate.
   * The resolver does its own caching, so this costs a function call.
   */
  private get context(): FilterContext {
    const resolver = this.deps.resolver?.() ?? null;
    return {
      favorites: this.deps.preferences.favorites,
      ...(resolver ? { resolver } : {}),
    };
  }

  /**
   * Apply a filter change. `rerenderFilters` is opt-out for controls that live *inside* the filter
   * panel and fire continuously (the height slider): rebuilding the panel would replace the very
   * element being dragged, ending the drag after one event.
   */
  private setFilter(patch: Partial<FloraFilter>, options: { rerenderFilters?: boolean } = {}): void {
    this.filter = { ...this.filter, ...patch };
    this.renderFormRail();
    if (options.rerenderFilters !== false) this.renderFilters();
    else this.updateFilterToggle();
    this.renderResults();
  }

  /** Refresh just the Filters chip's active-count badge. */
  private updateFilterToggle(): void {
    const active = activeFilterCount(this.filter);
    this.filterToggle.innerHTML = `${icon('filter', 14)}<span>Filters${active > 0 ? ` · ${active}` : ''}</span>`;
    setClass(this.filterToggle, 'is-active', active > 0);
  }

  private toggleFilters(): void {
    this.filtersOpen = !this.filtersOpen;
    this.filtersEl.hidden = !this.filtersOpen;
    this.renderFilters();
  }

  /* -------------------------------- Form-factor rail ------------------------------- */

  private renderFormRail(): void {
    clear(this.formRail);
    const all = this.definitions;
    const isAll = this.filter.formFactors.length === 0;

    this.formRail.appendChild(
      el('button', {
        class: `lg-rail__item${isAll ? ' is-active' : ''}`,
        attrs: { type: 'button' },
        on: { click: () => this.setFilter({ formFactors: [] }) },
        text: `All`,
      }),
    );

    for (const form of FORM_FACTORS) {
      const count = all.filter((d) => formFactorOf(d) === form).length;
      if (count === 0) continue;
      const active = this.filter.formFactors.includes(form);
      this.formRail.appendChild(
        el(
          'button',
          {
            class: `lg-rail__item${active ? ' is-active' : ''}`,
            attrs: { type: 'button', 'aria-pressed': active },
            on: {
              click: () =>
                this.setFilter({ formFactors: toggleFacet<FormFactor>(this.filter.formFactors, form) }),
            },
          },
          formFactorLabel(form),
          el('span', { class: 'lg-rail__count', text: String(count) }),
        ),
      );
    }
  }

  /* ---------------------------------- Filter panel --------------------------------- */

  private renderFilters(): void {
    this.updateFilterToggle();
    if (!this.filtersOpen) return;

    clear(this.filtersEl);
    const scoped = this.definitions;
    const context = this.context;

    // First in the panel when there is a site to compare against, and absent entirely when there is
    // not — the same rule the winter section follows in the environment tray.
    if (context.resolver) {
      this.filtersEl.appendChild(
        this.facetRow('This site', [
          ...(['ideal', 'suitable', 'marginal', 'outside-range'] as const).map((rating) =>
            this.chip(
              suitabilityBadge(rating),
              this.filter.suitability.includes(rating),
              scoped.filter((d) => ratingOf(d, context) === rating).length,
              () => this.setFilter({ suitability: toggleFacet(this.filter.suitability, rating) }),
            ),
          ),
          this.chip('Native here', this.filter.nativeOnly, null, () =>
            this.setFilter({ nativeOnly: !this.filter.nativeOnly }),
          ),
        ]),
      );
    }

    this.filtersEl.appendChild(
      this.facetRow(
        'Seasonal colour',
        COLOR_FAMILIES.filter((family) => scoped.some((d) => colorFamiliesOf(d).includes(family))).map(
          (family) =>
            el(
              'button',
              {
                class: `lg-swatch${this.filter.colorFamilies.includes(family) ? ' is-active' : ''}`,
                title: colorFamilyLabel(family),
                attrs: { type: 'button', 'aria-label': colorFamilyLabel(family) },
                on: {
                  click: () =>
                    this.setFilter({
                      colorFamilies: toggleFacet<ColorFamily>(this.filter.colorFamilies, family),
                    }),
                },
              },
              el('span', {
                class: 'lg-swatch__dot',
                style: { background: COLOR_FAMILY_SWATCHES[family] },
              }),
            ),
        ),
      ),
    );

    this.filtersEl.appendChild(
      this.facetRow(
        'Genus',
        genusFacets(scoped).map((facet) =>
          this.chip(`${facet.label}`, this.filter.genera.includes(facet.value), facet.count, () =>
            this.setFilter({ genera: toggleFacet(this.filter.genera, facet.value) }),
          ),
        ),
      ),
    );

    this.filtersEl.appendChild(
      this.facetRow(
        'Category',
        FLORA_CATEGORIES.filter((category) => scoped.some((d) => d.category === category)).map((category) =>
          this.chip(
            categoryLabel(category),
            this.filter.categories.includes(category),
            scoped.filter((d) => d.category === category).length,
            () => this.setFilter({ categories: toggleFacet(this.filter.categories, category) }),
          ),
        ),
      ),
    );

    this.filtersEl.appendChild(
      this.facetRow(
        'Sun',
        (['full-sun', 'part-sun', 'part-shade', 'full-shade'] as const)
          .filter((sun) => scoped.some((d) => d.sunlight.includes(sun)))
          .map((sun) =>
            this.chip(sun.replace('-', ' '), this.filter.sunlight.includes(sun), null, () =>
              this.setFilter({ sunlight: toggleFacet(this.filter.sunlight, sun) }),
            ),
          ),
      ),
    );

    this.filtersEl.appendChild(
      this.facetRow(
        'Moisture',
        (['dry', 'average', 'moist', 'wet'] as const)
          .filter((moisture) => scoped.some((d) => d.moisture.includes(moisture)))
          .map((moisture) =>
            this.chip(moisture, this.filter.moisture.includes(moisture), null, () =>
              this.setFilter({ moisture: toggleFacet(this.filter.moisture, moisture) }),
            ),
          ),
      ),
    );

    this.filtersEl.appendChild(
      this.facetRow(
        'Traits',
        tagFacets(scoped)
          .slice(0, 12)
          .map((facet) =>
            this.chip(facet.label, this.filter.tags.includes(facet.value), facet.count, () =>
              this.setFilter({ tags: toggleFacet(this.filter.tags, facet.value) }),
            ),
          ),
      ),
    );

    const tallest = Math.ceil(Math.max(...scoped.map((d) => d.growth.matureHeightMeters.typical), 1));
    const heightValue = this.filter.maxHeightMeters ?? tallest;
    const heightReadout = el('span', {
      class: 'lg-facet__value',
      text: this.filter.maxHeightMeters === null ? 'Any' : formatLength(this.filter.maxHeightMeters, this.unit),
    });
    this.filtersEl.appendChild(
      this.facetRow('Mature height at most', [
        el('input', {
          class: 'lg-slider',
          attrs: { type: 'range', min: '1', max: String(tallest), step: '1', value: String(heightValue) },
          on: {
            input: (event) => {
              const value = Number((event.target as HTMLInputElement).value);
              const limit = value >= tallest ? null : value;
              heightReadout.textContent = limit === null ? 'Any' : formatLength(limit, this.unit);
              this.setFilter({ maxHeightMeters: limit }, { rerenderFilters: false });
            },
          },
        }),
        heightReadout,
      ]),
    );

    this.filtersEl.appendChild(
      el(
        'div',
        { class: 'lg-filters__foot' },
        this.chip('Favorites only', this.filter.favoritesOnly, null, () =>
          this.setFilter({ favoritesOnly: !this.filter.favoritesOnly }),
        ),
        el('button', {
          class: 'lg-link',
          text: 'Clear all',
          attrs: { type: 'button' },
          on: { click: () => this.setFilter(defaultFloraFilter()) },
        }),
      ),
    );
  }

  private facetRow(label: string, children: Child[]): HTMLElement {
    return el(
      'div',
      { class: 'lg-facet' },
      el('span', { class: 'lg-facet__label', text: label }),
      el('div', { class: 'lg-facet__values' }, ...children),
    );
  }

  private chip(label: string, active: boolean, count: number | null, onClick: () => void): HTMLElement {
    return el(
      'button',
      {
        class: `lg-chip${active ? ' is-active' : ''}`,
        attrs: { type: 'button', 'aria-pressed': active },
        on: { click: onClick },
      },
      label,
      count !== null && el('span', { class: 'lg-chip__count', text: String(count) }),
    );
  }

  private select<T extends string>(
    label: string,
    options: readonly { value: T; label: string }[],
    value: T,
    onChange: (value: T) => void,
  ): HTMLElement {
    const selectEl = el('select', {
      class: 'lg-select__control',
      attrs: { 'aria-label': label },
      on: { change: (event) => onChange((event.target as HTMLSelectElement).value as T) },
    });
    for (const option of options) {
      selectEl.appendChild(
        el('option', { text: option.label, attrs: { value: option.value, selected: option.value === value } }),
      );
    }
    return el('label', { class: 'lg-select' }, el('span', { class: 'lg-select__label', text: label }), selectEl);
  }

  /* ------------------------------------ Results ------------------------------------ */

  private get unit(): 'meters' | 'feet' {
    return this.deps.preferences.unit;
  }

  private renderResults(): void {
    const context = this.context;
    const matched = filterFlora(this.definitions, this.filter, context);
    const sorted = sortFlora(matched, this.sort, context);
    const groups = groupFlora(sorted, this.groupBy);

    this.countEl.textContent = `${matched.length}/${this.deps.catalog.size}`;
    this.renderSiteSummary(context);
    this.renderRecent();

    clear(this.resultsEl);
    if (matched.length === 0) {
      this.resultsEl.appendChild(
        el(
          'div',
          { class: 'lg-empty' },
          el('p', { text: 'No plants match these filters.' }),
          el('button', {
            class: 'lg-link',
            text: 'Clear filters',
            attrs: { type: 'button' },
            on: { click: () => this.setFilter(defaultFloraFilter()) },
          }),
        ),
      );
      return;
    }

    for (const group of groups) {
      if (group.items.length === 0) continue;
      const section = el('section', { class: 'lg-group' });
      if (this.groupBy !== 'none') {
        section.appendChild(
          el(
            'h3',
            { class: 'lg-group__title' },
            group.label,
            el('span', { class: 'lg-group__count', text: String(group.items.length) }),
          ),
        );
      }
      const grid = el('div', { class: 'lg-grid' });
      for (const definition of group.items) grid.appendChild(this.tile(definition));
      section.appendChild(grid);
      this.resultsEl.appendChild(section);
    }

    this.reanchorCard();
  }

  /**
   * Every re-render replaces the tiles, and with them the button an open card is hanging off. Hand
   * the card the new button for the same plant so favoriting or filtering does not dismiss it — and
   * close it when the plant is no longer in the results at all.
   */
  private reanchorCard(): void {
    const id = this.card.openId;
    if (id === null) return;
    const escaped = typeof CSS !== 'undefined' && CSS.escape ? CSS.escape(id) : id;
    const info = this.resultsEl.querySelector<HTMLElement>(`[data-flora-info="${escaped}"]`);
    if (info) this.card.reanchor(info);
    else this.card.close();
  }

  /**
   * `38 ideal or suitable · 11 marginal · 5 outside range` — the catalog read against this site.
   *
   * Ideal and suitable are counted together deliberately. The distinction between them is real and
   * belongs on the tile; at the top of the palette the useful split is "plants I can plant here" from
   * "plants that need a conversation first", and three numbers say that where four blur it.
   *
   * Each figure is a one-press filter, because a count you cannot act on is decoration.
   */
  private renderSiteSummary(context: FilterContext): void {
    const tally = tallyForSite(this.definitions, context);
    clear(this.siteSummary);
    this.siteSummary.hidden = tally === null;
    if (!tally) return;

    const bands: { label: string; count: number; ratings: SuitabilityRating[]; tone: string }[] = [
      { label: 'ideal or suitable', count: tally.ideal + tally.suitable, ratings: ['ideal', 'suitable'], tone: 'good' },
      { label: 'marginal', count: tally.marginal, ratings: ['marginal'], tone: 'warn' },
      { label: 'outside range', count: tally.outsideRange, ratings: ['outside-range'], tone: 'bad' },
    ];

    for (const band of bands) {
      if (band.count === 0) continue;
      const active =
        this.filter.suitability.length === band.ratings.length &&
        band.ratings.every((rating) => this.filter.suitability.includes(rating));
      this.siteSummary.appendChild(
        el(
          'button',
          {
            class: `lg-sitecount lg-sitecount--${band.tone}${active ? ' is-active' : ''}`,
            attrs: { type: 'button', 'aria-pressed': active },
            title: `Show only the ${band.label} plants for this site`,
            on: { click: () => this.setFilter({ suitability: active ? [] : band.ratings }) },
          },
          el('strong', { text: String(band.count) }),
          el('span', { text: band.label }),
        ),
      );
    }
  }

  private renderRecent(): void {
    clear(this.recentEl);
    const recent = this.deps.preferences.recent
      .map((id) => this.deps.catalog.find(id))
      .filter((d): d is FloraDefinition => d !== undefined)
      .slice(0, 6);
    if (recent.length === 0) return;

    this.recentEl.appendChild(el('h3', { class: 'lg-group__title', text: 'Recent' }));
    const strip = el('div', { class: 'lg-strip' });
    for (const definition of recent) {
      strip.appendChild(
        el('button', {
          class: `lg-strip__item${definition.id === this.deps.activeId() ? ' is-active' : ''}`,
          html: floraThumbnailSvg(definition, { season: this.deps.season(), chrome: false }),
          title: displayName(definition),
          attrs: { type: 'button', 'aria-label': displayName(definition) },
          on: { click: () => this.deps.onPick(definition.id) },
        }),
      );
    }
    this.recentEl.appendChild(strip);
  }

  private tile(definition: FloraDefinition): HTMLElement {
    const isActive = definition.id === this.deps.activeId();
    const isFavorite = this.deps.preferences.isFavorite(definition.id);
    const height = definition.growth.matureHeightMeters.typical;
    const spread = definition.growth.matureSpreadMeters.typical;

    // Mirrors the star in the opposite corner: the two things you can do to a tile without
    // committing to placing the plant. It opens the full catalog record beside the palette.
    const info = el('button', {
      class: `lg-tile__info${this.card.openId === definition.id ? ' is-active' : ''}`,
      html: icon('info', 14),
      title: `Everything on record for ${displayName(definition)}`,
      attrs: {
        type: 'button',
        'aria-label': `Plant record for ${displayName(definition)}`,
        // Stringified deliberately: `el` drops `false` attributes, and a disclosure button has to
        // carry `aria-expanded` in both states for a screen reader to announce it as one.
        'aria-expanded': String(this.card.openId === definition.id),
        'data-flora-info': definition.id,
      },
      on: {
        click: (event) => {
          event.stopPropagation();
          this.card.toggle(definition, info);
        },
      },
    });

    const star = el('button', {
      class: `lg-tile__star${isFavorite ? ' is-active' : ''}`,
      html: isFavorite ? filledIcon('star', 14) : icon('star', 14),
      title: isFavorite ? 'Remove from favorites' : 'Add to favorites',
      attrs: { type: 'button', 'aria-label': 'Toggle favorite', 'aria-pressed': isFavorite },
      on: {
        click: (event) => {
          event.stopPropagation();
          this.deps.preferences.toggleFavorite(definition.id);
          this.renderResults();
        },
      },
    });

    return el(
      'button',
      {
        class: `lg-tile${isActive ? ' is-active' : ''}`,
        title: `${definition.botanicalName}${definition.cultivar ? ` '${definition.cultivar}'` : ''}`,
        attrs: { type: 'button', 'data-flora-id': definition.id, 'aria-pressed': isActive },
        on: { click: () => this.deps.onPick(definition.id) },
      },
      el('span', { class: 'lg-tile__thumb', html: floraThumbnailSvg(definition, { season: this.deps.season() }) }),
      info,
      star,
      this.suitabilityBadge(definition),
      el('span', { class: 'lg-tile__name', text: displayName(definition) }),
      el('span', { class: 'lg-tile__latin', text: definition.botanicalName }),
      el('span', {
        class: 'lg-tile__size',
        text: `${formatLength(height, this.unit)} × ${formatLength(spread, this.unit)}`,
      }),
    );
  }

  /**
   * The tile's site badge — or nothing at all.
   *
   * Only marginal, out-of-range and locally native plants get one. A badge on every tile would be
   * noise on a grid where most plants are simply fine, and "Suitable" is the answer you get by not
   * being warned. The tooltip carries the reason, because the badge alone says a plant is a problem
   * without saying which problem, which is the least useful half of the message.
   */
  private suitabilityBadge(definition: FloraDefinition): HTMLElement | false {
    const context = this.context;
    if (!context.resolver) return false;
    const rating = ratingOf(definition, context);
    if (rating === undefined) return false;

    if (rating === 'marginal' || rating === 'outside-range') {
      const resolved = context.resolver.resolve(definition);
      const warning = primaryWarning(resolved.suitability);
      return el('span', {
        class: `lg-tile__fit lg-tile__fit--${rating === 'marginal' ? 'warn' : 'bad'}`,
        text: suitabilityBadge(rating),
        title: warning ? `${suitabilityLabel(rating)} — ${warning.message}` : suitabilityLabel(rating),
      });
    }

    // The one positive badge, because it answers a question people actively search for and the
    // catalog's `native` tag cannot: native *where*. See `FloraNativeRangeDefinition`.
    if (nativeStatusOf(definition, context) === 'native') {
      return el('span', {
        class: 'lg-tile__fit lg-tile__fit--native',
        text: 'Native',
        title: 'Indigenous to this site’s region.',
      });
    }
    return false;
  }

  /**
   * Light the ⓘ that owns the open card and dim the rest. Done in place rather than by re-rendering
   * the results, so opening a record never moves the tile you just pressed.
   */
  private markInfoActive(): void {
    for (const button of this.resultsEl.querySelectorAll<HTMLElement>('[data-flora-info]')) {
      const isOpen = button.dataset.floraInfo === this.card.openId;
      setClass(button, 'is-active', isOpen);
      button.setAttribute('aria-expanded', String(isOpen));
    }
  }

  dispose(): void {
    this.card.dispose();
    this.root.remove();
  }
}
